import { useState, useMemo, useEffect } from "react";
import { CommissionStatus } from "../constants";

const isContractPendingStatus = (contract) =>
  String(contract?.status ?? "")
    .trim()
    .toLowerCase() === "pending";

// Multi-select of contract numbers. Only ever acts on selections that are still
// pending AND present in the current results, so stale selections from a prior
// search quietly fall out of the derived totals.
export function useContractSelection(contracts) {
  const [selected, setSelected] = useState(() => new Set());
  const [confirmDecline, setConfirmDecline] = useState(false);

  const activeSelection = useMemo(
    () =>
      contracts.filter(
        (c) =>
          c.commissionStatus === CommissionStatus.Pending &&
          selected.has(c.number),
      ),
    [contracts, selected],
  );

  const selCount = activeSelection.length;
  const selAmount = activeSelection.reduce((s, c) => s + c.netPrice, 0);
  const approvable = activeSelection.filter(
    (c) => c.agent && !isContractPendingStatus(c),
  );
  const blocked = activeSelection.filter(
    (c) => !c.agent || isContractPendingStatus(c),
  );
  const approveCount = approvable.length;
  const blockedCount = selCount - approveCount;
  const approveAmount = approvable.reduce((s, c) => s + c.netPrice, 0);
  const blockedAmount = blocked.reduce((s, c) => s + c.netPrice, 0);

  // If the visible selection empties out, drop the confirm prompt.
  useEffect(() => {
    if (selCount === 0 && confirmDecline) setConfirmDecline(false);
  }, [selCount, confirmDecline]);

  const toggleOne = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleGroup = (ids, select) =>
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (select ? next.add(id) : next.delete(id)));
      return next;
    });

  const clearSelection = () => {
    setSelected(new Set());
    setConfirmDecline(false);
  };

  return {
    selected,
    setSelected,
    confirmDecline,
    setConfirmDecline,
    activeSelection,
    selCount,
    selAmount,
    approvable,
    approveCount,
    blockedCount,
    approveAmount,
    blockedAmount,
    toggleOne,
    toggleGroup,
    clearSelection,
  };
}
